// When the app launches with a ?leadId=/?pqid=, it loads the household from
// Salesforce. If the browser already holds edits that never made it to
// Salesforce (a save failed during an outage, the close-time beacon was dropped,
// the tab crashed), a blind "replace the store with the loaded record" silently
// discards those edits. This decides who wins.
//
// Kept pure and dependency-free so it can be unit-tested in isolation.
//
// Returns:
//   'sf'    adopt the loaded Salesforce record (default; it carries the
//           canonical ids and, when nothing local is at stake, is authoritative)
//   'local' keep the browser's data (unsynced edits) and let the autosave loop
//           push it up to Salesforce
//
// Inputs (all derived from the SAME pqDigest used by the autosave loop):
//   localDigest     digest of the PQ data the browser held at page load
//   sfDigest        digest of the record just loaded from Salesforce
//   syncedDigest    digest last CONFIRMED saved to Salesforce, persisted across
//                   reloads; '' or null if none was ever recorded
//   localHasContent whether the browser holds a real household worth keeping
//                   (a client name or a prior Salesforce id) rather than a
//                   blank/fresh store
export function resolveHydration({ localDigest, sfDigest, syncedDigest, localHasContent }) {
  // Nothing local worth protecting (fresh browser, or a different household was
  // just switched into the URL, which clears the cache) — take Salesforce.
  if (!localHasContent) return 'sf';

  // Local already equals the loaded record — take Salesforce; they are
  // identical and it carries the canonical record ids.
  if (localDigest === sfDigest) return 'sf';

  if (syncedDigest) {
    // Local matches the last state we confirmed saved, so the difference lives
    // on the Salesforce side (the record was edited elsewhere since). There are
    // no local edits to lose — take the newer Salesforce record.
    if (localDigest === syncedDigest) return 'sf';
    // Local has drifted from the last saved state: genuine unsynced edits. Keep
    // them.
    return 'local';
  }

  // No record of what was last synced (first load after this shipped, or storage
  // was cleared) and local differs from Salesforce. We cannot prove the local
  // data was ever saved, so preserve it rather than silently discard edits; the
  // autosave loop reconciles it up to Salesforce.
  return 'local';
}
